import React from 'react';

interface FormStatusBadgeProps {
  /** Whether the consent form has been completed */
  isComplete: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Whether to show the full label or just icon */
  iconOnly?: boolean;
  /** Optional click handler */
  onClick?: () => void;
}

/**
 * FormStatusBadge Component
 *
 * Displays the status of consent form completion.
 * Used in admin/crew appointment views.
 */
const FormStatusBadge: React.FC<FormStatusBadgeProps> = ({
  isComplete,
  size = 'md',
  iconOnly = false,
  onClick,
}) => {
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
  };

  const iconSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
  };

  if (isComplete) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeClasses[size]} ${
          onClick ? 'cursor-pointer hover:opacity-80' : ''
        } bg-green-100 text-green-700`}
        onClick={onClick}
        title="Consent form completed"
      >
        <svg
          className={iconSizeClasses[size]}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {!iconOnly && <span>Form Complete</span>}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeClasses[size]} ${
        onClick ? 'cursor-pointer hover:opacity-80' : ''
      } bg-yellow-100 text-yellow-700`}
      onClick={onClick}
      title="Consent form required"
    >
      <svg
        className={iconSizeClasses[size]}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      {!iconOnly && <span>Form Required</span>}
    </span>
  );
};

export default FormStatusBadge;
